import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Pill, Search, RefreshCw, IndianRupee, CheckCircle2, XCircle,
  AlertCircle, Clock, User, Award, Package as PackageIcon,
} from 'lucide-react';

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'paid', label: 'Paid' },
  { key: 'partial', label: 'Partial Paid' },
  { key: 'cancelled', label: 'Cancelled' },
];
const PAGE_LIMIT = 50;

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDT = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(s).slice(0, 19); }
};

const derivePayStatus = (sale) => {
  if (!sale) return { key: 'unknown', label: '—', cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  if (sale.status === 'cancelled') {
    return { key: 'cancelled', label: 'Cancelled', cls: 'bg-rose-100 text-rose-700 border-rose-200' };
  }
  const ps = String(sale.totals?.payment_status || '').toLowerCase();
  const paid = Number(sale.totals?.paid || 0);
  const total = Number(sale.totals?.total || 0);
  // Fully paid → paid; anything else (partial, unpaid, missing) → partial paid
  const isFullyPaid = ps === 'paid' || (total > 0 && paid >= total);
  if (isFullyPaid) {
    return { key: 'paid', label: 'Paid', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' };
  }
  return { key: 'partial', label: 'Partial Paid', cls: 'bg-amber-100 text-amber-700 border-amber-200' };
};

const StatusPill = ({ sale }) => {
  const s = derivePayStatus(sale);
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>;
};

export default function PharmacySalesPage({ user, onLogout }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [bridgeState, setBridgeState] = useState(null);

  const load = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = { page: opts.page ?? page, limit: PAGE_LIMIT };
      if (q) params.q = q;
      if (statusFilter === 'cancelled') params.status = 'cancelled';
      else if (statusFilter === 'paid') params.payment_status = 'paid';
      else if (statusFilter === 'partial') params.payment_status = 'partial';
      const res = await api.get('/pharmacy/sales', { params });
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pharmacy sales');
    } finally {
      setLoading(false);
    }
  }, [page, q, statusFilter]);

  const loadBridgeState = useCallback(async () => {
    try {
      const res = await api.get('/pharmacy/bridge/state');
      setBridgeState(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { setPage(0); load({ page: 0 }); loadBridgeState(); }, [statusFilter, load, loadBridgeState]);

  const runSearch = () => { setPage(0); load({ page: 0 }); };
  const refresh = () => { load(); loadBridgeState(); };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5" data-testid="pharmacy-sales-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-sm">
              <Pill className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Pharmacy Sales</h1>
              <p className="text-sm text-gray-500">Inbound feed from Eden Pharmacy · 4% reward points auto-awarded</p>
            </div>
          </div>
          <Button onClick={refresh} variant="outline" className="gap-2" data-testid="pharm-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Bridge state KPI strip */}
        {bridgeState && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3" data-testid="bridge-state">
            <div className="rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total Received</div>
              <div className="text-2xl font-extrabold text-slate-900 leading-none mt-1">{bridgeState.total_received}</div>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-100 border border-emerald-200 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Successful</div>
              <div className="text-2xl font-extrabold text-emerald-800 leading-none mt-1">{bridgeState.total_ok}</div>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-rose-50 to-red-100 border border-rose-200 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-rose-700">Failed</div>
              <div className="text-2xl font-extrabold text-rose-800 leading-none mt-1">{bridgeState.total_failed}</div>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-100 border border-amber-200 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Cancelled</div>
              <div className="text-2xl font-extrabold text-amber-800 leading-none mt-1">{bridgeState.total_cancelled}</div>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-fuchsia-50 to-pink-100 border border-fuchsia-200 p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-fuchsia-700">Points Awarded</div>
              <div className="text-2xl font-extrabold text-fuchsia-800 leading-none mt-1">{bridgeState.total_points_awarded}</div>
            </div>
          </div>
        )}

        {/* Search + status pills */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by receipt, patient or phone…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && runSearch()}
              className="pl-9"
              data-testid="pharm-search"
            />
          </div>
          <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1 bg-white">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className={`text-xs font-medium px-3 py-1.5 rounded-md transition ${
                  statusFilter === s.key
                    ? 'bg-teal-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`pharm-status-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Receipt</th>
                <th className="text-left px-4 py-2 font-medium">Patient</th>
                <th className="text-right px-4 py-2 font-medium">Total</th>
                <th className="text-right px-4 py-2 font-medium">Paid</th>
                <th className="text-center px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Points</th>
                <th className="text-left px-4 py-2 font-medium">Received</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="7" className="text-center py-8 text-gray-400 italic">No pharmacy sales yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr
                  key={r.sale_id}
                  onClick={() => setDetail(r)}
                  className="border-t border-gray-100 hover:bg-teal-50/40 cursor-pointer"
                  data-testid={`pharm-row-${r.sale_id}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-teal-700">{r.receipt_no || r.sale_id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{r.local_patient_name || r.patient?.name || '—'}</div>
                    <div className="text-[11px] text-gray-500">{r.patient?.phone || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtINR(r.totals?.total)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 font-semibold">{fmtINR(r.totals?.paid)}</td>
                  <td className="px-4 py-3 text-center"><StatusPill sale={r} /></td>
                  <td className="px-4 py-3 text-right">
                    {r.reward_last_action === 'awarded' && (
                      <span className="text-fuchsia-700 font-semibold">+{r.reward_points_earned_last}</span>
                    )}
                    {r.reward_last_action === 'reversed' && (
                      <span className="text-rose-700 font-semibold">{r.reward_points_earned_last}</span>
                    )}
                    {!r.reward_last_action && <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[11px] text-gray-500">{fmtDT(r.received_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50 text-sm">
            <div className="text-gray-600">
              {total > 0 ? `Showing ${page * PAGE_LIMIT + 1}–${page * PAGE_LIMIT + rows.length} of ${total}` : ' '}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" disabled={page === 0 || loading} onClick={() => { const np = page - 1; setPage(np); load({ page: np }); }}>Prev</Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * PAGE_LIMIT >= total || loading} onClick={() => { const np = page + 1; setPage(np); load({ page: np }); }}>Next</Button>
            </div>
          </div>
        </div>

        {/* Detail Dialog */}
        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pharm-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pill className="w-5 h-5 text-teal-600" />
                {detail?.receipt_no || detail?.sale_id}
                <StatusPill sale={detail} />
              </DialogTitle>
              <DialogDescription>Pharmacy sale mirrored from Eden Pharmacy · {fmtDT(detail?.received_at)}</DialogDescription>
            </DialogHeader>

            {detail && (
              <div className="space-y-4">
                {/* Patient */}
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1"><User className="w-3.5 h-3.5" /> Patient</div>
                  <div className="font-semibold">{detail.local_patient_name || detail.patient?.name || 'Unmatched'}</div>
                  <div className="text-sm text-gray-600">{detail.patient?.phone || ''}</div>
                  {!detail.local_patient_id && (
                    <div className="text-[11px] text-rose-600 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" /> No matching Appointy patient — reward points skipped.</div>
                  )}
                </div>

                {/* Totals */}
                <div className="grid grid-cols-4 gap-2">
                  <div className="rounded-lg bg-slate-50 border border-slate-100 p-2">
                    <div className="text-[9px] font-semibold uppercase text-slate-500">Subtotal</div>
                    <div className="font-bold">{fmtINR(detail.totals?.subtotal)}</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-100 p-2">
                    <div className="text-[9px] font-semibold uppercase text-slate-500">Discount</div>
                    <div className="font-bold text-orange-700">{fmtINR(detail.totals?.discount)}</div>
                  </div>
                  <div className="rounded-lg bg-slate-50 border border-slate-100 p-2">
                    <div className="text-[9px] font-semibold uppercase text-slate-500">Total</div>
                    <div className="font-bold">{fmtINR(detail.totals?.total)}</div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2">
                    <div className="text-[9px] font-semibold uppercase text-emerald-700">Paid</div>
                    <div className="font-bold text-emerald-800">{fmtINR(detail.totals?.paid)}</div>
                  </div>
                </div>

                {/* Reward outcome */}
                {detail.reward_last_action && (
                  <div className={`rounded-lg p-3 border ${detail.reward_last_action === 'awarded' ? 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase"><Award className="w-3.5 h-3.5" /> Reward Points</div>
                    <div className="mt-1 text-sm">
                      {detail.reward_last_action === 'awarded' && <>+{detail.reward_points_earned_last} points awarded (4% of paid amount)</>}
                      {detail.reward_last_action === 'reversed' && <>{detail.reward_points_earned_last} points reversed on cancellation</>}
                    </div>
                    {detail.reward_error && <div className="text-[11px] mt-1">⚠︎ {detail.reward_error}</div>}
                  </div>
                )}

                {/* Items */}
                {detail.items?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1"><PackageIcon className="w-3.5 h-3.5" /> Items</div>
                    <table className="w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
                      <thead className="bg-gray-50 text-gray-600 text-xs">
                        <tr>
                          <th className="text-left px-2 py-1.5">Medicine</th>
                          <th className="text-right px-2 py-1.5">Qty</th>
                          <th className="text-right px-2 py-1.5">Unit</th>
                          <th className="text-right px-2 py-1.5">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.items.map((it, i) => (
                          <tr key={i} className="border-t border-gray-100">
                            <td className="px-2 py-1.5">
                              {it.medicine || it.sku}
                              {it.batch_display && <div className="text-[10px] text-gray-500">{it.batch_display}</div>}
                            </td>
                            <td className="px-2 py-1.5 text-right">{it.qty}</td>
                            <td className="px-2 py-1.5 text-right">{fmtINR(it.unit_price)}</td>
                            <td className="px-2 py-1.5 text-right font-semibold">{fmtINR(it.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Payments */}
                {detail.payments?.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-1"><IndianRupee className="w-3.5 h-3.5" /> Payments</div>
                    <div className="flex flex-wrap gap-2">
                      {detail.payments.map((p, i) => (
                        <div key={i} className="bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 text-xs">
                          <span className="font-semibold text-emerald-800">{p.method_code || p.method_name || '—'}</span>
                          <span className="ml-1 text-emerald-700">{fmtINR(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Meta */}
                <div className="grid grid-cols-2 gap-2 text-[11px] text-gray-500">
                  <div><Clock className="inline w-3 h-3 mr-1" />Created: {fmtDT(detail.created_at)}</div>
                  <div><Clock className="inline w-3 h-3 mr-1" />Updated: {fmtDT(detail.updated_at)}</div>
                  <div><User className="inline w-3 h-3 mr-1" />Cashier: {detail.actor?.user_name || '—'}</div>
                  <div className="font-mono">sale_id: {detail.sale_id}</div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
